import React from "react";
import {
  Grid,
  Box,
  Autocomplete,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Select,
} from "@mui/material";
import {
  CheckCircleRounded,
  CreateOutlined as CreateIcon,
  AddCircleOutlined as AddCircleIcon,
  CancelOutlined as CancelIcon,
} from "@mui/icons-material";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TagsDialog from "../Dialogs/TagsDialog";
import SecondaryMergeResource from "../Molecules/Rule/SecondaryMergeResource";

// SortableChip component for production use - moved outside to avoid hooks issues
const ProductionSortableChip = ({
  option,
  index,
  getTagProps,
  resultValues,
  resultValuesLength,
  setTagId,
  onClickMergeTag,
  columnsArr,
  resourceItem,
}: {
  option: string;
  index: number;
  getTagProps: any;
  resultValues: any;
  resultValuesLength: number;
  setTagId: (tagId: string) => void;
  onClickMergeTag: (
    option: string,
    columnsArr: any[],
    resourceName: string,
    rId: any,
  ) => void;
  columnsArr: any[];
  resourceItem: any;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  // Extract only onDelete from getTagProps to avoid conflicts
  const { onDelete } = getTagProps({ index });

  // Custom delete handler to prevent conflicts with drag listeners
  const handleDelete = (e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(e);
    }
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Chip
        label={option}
        onDelete={onDelete}
        className={`${resultValuesLength ? "sep-position" : ""}`}
        style={{
          cursor: "grab",
          userSelect: "none",
        }}
        avatar={
          <Box>
            <IconButton
              size="small"
              aria-label="info"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const cleanedOption = option.replace(" ", "");
                setTagId(cleanedOption);
                onClickMergeTag(
                  option,
                  columnsArr,
                  resourceItem?.resource_name,
                  resourceItem?.rId,
                );
              }}
            >
              {resultValuesLength >= 1 ? (
                <CreateIcon fontSize="small" />
              ) : (
                <AddCircleIcon fontSize="small" />
              )}
            </IconButton>

            <Tooltip
              placement="bottom"
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#e6f6ff",
                    padding: "13px 15px",
                    borderRadius: "8px",
                    boxShadow: "0 8px 4px 4px rgba(0,0,0,.22)",
                    "& .MuiTooltip-arrow": {
                      color: "#e6f6ff",
                    },
                  },
                },
              }}
              title={
                <Box className="tags-tooltip-parent">
                  {resultValues &&
                    resultValues.length >= 1 &&
                    resultValues?.map((item: any) => {
                      return (
                        <div className="chip" key={item}>
                          {item}
                        </div>
                      );
                    })}
                </Box>
              }
            >
              <span className={`${resultValuesLength >= 1 ? "counter" : ""}`}>
                {resultValuesLength > 0
                  ? resultValuesLength < 10
                    ? `0${resultValuesLength}`
                    : resultValuesLength
                  : ""}
              </span>
            </Tooltip>
          </Box>
        }
        deleteIcon={
          <span
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onPointerDown={handleDelete}
            style={{ cursor: "pointer" }}
          >
            <CancelIcon />
          </span>
        }
      />
    </div>
  );
};

// Props interface for the SelectedResourceList component
interface SelectedResourceListProps {
  selectedResourceIds: any[];
  selectedResourceColumns: any[];
  tagColResult: any[];
  showTagsDialog: any;
  tagId: string;
  selectedTags: any[];
  possibleMergeColumns: any[];
  selectedMergeType: any[];
  mergeType: string[];
  resourcesData: any[];
  resultData: any;
  secondaryMergeResource: any[];
  errors: any;
  isSelecting: boolean;
  onSelectResouceColumns: (rId: any, columns: any[]) => void;
  setTagId: (tagId: string) => void;
  onClickMergeTag: (
    option: string,
    columnsArr: any[],
    resourceName: string,
    rId: any,
  ) => void;
  renderSelectedResourcesPopper: (props: any, ids: string[]) => any;
  setShowTagsDialog: (dialog: any) => void;
  setTagColResult: (result: any) => void;
  setPossibleMergeColumns: (columns: any) => void;
  handleChangeMergeType: (e: any, rId: any) => void;
  setSecondaryMergeResource: (resource: any) => void;
  setIsLoaded: (loaded: boolean) => void;
  validateSecondaryResourceField: (
    name: any,
    index: number,
    element: string,
    value: any,
  ) => Promise<void>;
  setResultData: (data: any) => void;
}

const SelectedResourceList: React.FC<SelectedResourceListProps> = ({
  selectedResourceIds,
  selectedResourceColumns,
  tagColResult,
  showTagsDialog,
  tagId,
  selectedTags,
  possibleMergeColumns,
  selectedMergeType,
  mergeType,
  resourcesData,
  resultData,
  secondaryMergeResource,
  errors,
  isSelecting,
  onSelectResouceColumns,
  setTagId,
  onClickMergeTag,
  renderSelectedResourcesPopper,
  setShowTagsDialog,
  setTagColResult,
  setPossibleMergeColumns,
  handleChangeMergeType,
  setSecondaryMergeResource,
  setIsLoaded,
  validateSecondaryResourceField,
  setResultData,
}) => {
  // @dnd-kit sensors setup - moved to top level to follow Rules of Hooks
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // @dnd-kit drag handler for production autocomplete
  const handleProductionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Find the resource that contains this column
      const resourceId = active.data.current?.resourceId;
      if (resourceId) {
        const currentColumns =
          selectedResourceColumns?.length > 0
            ? selectedResourceColumns.find(
                (item: any) => item.rId === resourceId,
              )?.columns || []
            : [];

        const oldIndex = currentColumns.findIndex(
          (column: string) => column === active.id,
        );
        const newIndex = currentColumns.findIndex(
          (column: string) => column === over.id,
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newColumns = arrayMove(currentColumns, oldIndex, newIndex);
          onSelectResouceColumns(resourceId, newColumns);
        }
      }
    }
  };

  return (
    <div className="merge-definition-wrapper">
      {selectedResourceIds.length > 0 && (
        <div className="selected-resource-list">
          <h4>Select Resource column for Merge definition</h4>
          <div className="merge-def-inner">
            <Grid container>
              {selectedResourceIds.map((resourceItem: any, idx: any) => {
                const columnsArr =
                  resourceItem?.resource_column_properties?.resource_columns
                    ?.map((item: any) => {
                      if (item?.is_active) {
                        return item.column_name === "file_name"
                          ? null
                          : item.column_name;
                      }
                    })
                    .filter((filterItem: any) => filterItem);
                return (
                  <Grid
                    item
                    xs
                    md={12}
                    lg={12}
                    key={resourceItem?.id}
                    className={`data-grid`}
                  >
                    <Grid container rowSpacing={2.5} columnSpacing={4}>
                      <Grid
                        item
                        xs
                        md={12}
                        lg={8}
                        className="position-relative"
                      >
                        <Box
                          className="label-text"
                          sx={{ paddingTop: "10px", paddingRight: "16px" }}
                        >
                          {`${resourceItem?.resource_name} ${
                            idx === 0
                              ? "(Primary Resource)"
                              : "(Secondary Resource)"
                          }  `}
                        </Box>
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => {
                            // Add resourceId to the event data for proper handling
                            const modifiedEvent = {
                              ...event,
                              active: {
                                ...event.active,
                                data: {
                                  ...event.active.data,
                                  current: {
                                    ...event.active.data.current,
                                    resourceId: resourceItem?.rId,
                                  },
                                },
                              },
                            };
                            handleProductionDragEnd(modifiedEvent);
                          }}
                        >
                          <Autocomplete
                            id={`autocomplete-selected-resources-${idx}`}
                            PopperComponent={(props) =>
                              renderSelectedResourcesPopper(props, idx)
                            }
                            fullWidth={true}
                            className="form-control-autocomplete  alternative-2"
                            disabled={Boolean(isSelecting)}
                            multiple
                            options={columnsArr?.length > 0 ? columnsArr : []}
                            getOptionLabel={(option) => option}
                            disableCloseOnSelect
                            onChange={(key: any, value: any) =>
                              onSelectResouceColumns(resourceItem?.rId, value)
                            }
                            value={
                              selectedResourceColumns?.length > 0
                                ? selectedResourceColumns.find(
                                    (item: any) =>
                                      item.rId === resourceItem?.rId,
                                  )?.columns
                                : []
                            }
                            renderInput={(params) => (
                              <>
                                <div className="autocomplete-chips-direction">
                                  <TextField
                                    {...params}
                                    variant="outlined"
                                    placeholder="Select..."
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                  />
                                </div>
                              </>
                            )}
                            renderOption={(props, option, { selected }) => (
                              <MenuItem
                                {...props}
                                key={option}
                                value={option}
                                sx={{ justifyContent: "space-between" }}
                              >
                                {option}
                                {selected ? (
                                  <CheckCircleRounded color="info" />
                                ) : null}
                              </MenuItem>
                            )}
                            renderTags={(value, getTagProps) => (
                              <SortableContext
                                items={value}
                                strategy={horizontalListSortingStrategy}
                              >
                                <div className="align-from-left flex-reverse flex-reverse-01">
                                  {value.map((option, index) => {
                                    const resultValues =
                                      tagColResult &&
                                      tagColResult.find(
                                        (obj: any) =>
                                          obj.key === option &&
                                          obj.id === resourceItem?.rId,
                                      )?.values;

                                    const resultValuesLength =
                                      resultValues && resultValues.length;

                                    return (
                                      <ProductionSortableChip
                                        key={option}
                                        option={option}
                                        index={index}
                                        getTagProps={getTagProps}
                                        resultValues={resultValues}
                                        resultValuesLength={resultValuesLength}
                                        setTagId={setTagId}
                                        onClickMergeTag={onClickMergeTag}
                                        columnsArr={columnsArr}
                                        resourceItem={resourceItem}
                                      />
                                    );
                                  })}
                                </div>
                              </SortableContext>
                            )}
                          />
                        </DndContext>
                        {(() => {
                          // Check if any dialog should be visible for this resource
                          const resourcePrefix = `${resourceItem?.resource_name}_`;
                          const allKeys = Object.keys(showTagsDialog);
                          const resourceKeys = allKeys.filter((key) =>
                            key.startsWith(resourcePrefix),
                          );
                          const visibleKey = resourceKeys.find(
                            (key) => showTagsDialog[key] === true,
                          );
                          const isVisible = !!visibleKey;

                          return (
                            <div
                              className={`mergetag-popup ${tagId}`}
                              style={{
                                display: isVisible ? "block" : "none",
                              }}
                            >
                              <TagsDialog
                                setShowDialog={setShowTagsDialog}
                                selectedTags={selectedTags}
                                setTagColResult={setTagColResult}
                                tagColResult={tagColResult}
                                setPossibleMergeColumns={
                                  setPossibleMergeColumns
                                }
                                possibleMergeColumns={possibleMergeColumns}
                                resourceName={resourceItem?.resource_name}
                                tagKey={tagId}
                              />
                            </div>
                          );
                        })()}
                      </Grid>
                      <Grid
                        item
                        xs
                        md={12}
                        lg={3}
                        className="position-relative"
                      >
                        {idx === 0 ? null : (
                          <>
                            <Box
                              className="label-text"
                              sx={{
                                paddingTop: "10px",
                                paddingRight: "16px",
                              }}
                            >
                              Merge Type
                            </Box>
                            <Select
                              MenuProps={{
                                disableScrollLock: true,
                              }}
                              title="Merge type"
                              defaultValue="outer"
                              value={
                                selectedMergeType &&
                                selectedMergeType.length > 0
                                  ? (selectedMergeType.find(
                                      (item: any) =>
                                        item?.rId === resourceItem?.rId,
                                    )?.merge_type ?? "outer")
                                  : "outer"
                              }
                              name="merge_type"
                              style={{ width: "100%", height: 35 }}
                              onChange={(e) =>
                                handleChangeMergeType(e, resourceItem?.rId)
                              }
                              className={`form-control-autocomplete form-control-autocomplete-1`}
                            >
                              {mergeType.map((type: string) => (
                                <MenuItem key={type} value={type}>
                                  <span style={{ textTransform: "capitalize" }}>
                                    {type}
                                  </span>
                                </MenuItem>
                              ))}
                            </Select>
                          </>
                        )}
                      </Grid>
                      <SecondaryMergeResource
                        resourcesData={resourcesData}
                        resultData={resultData}
                        setResultData={setResultData}
                        dataSetTypeIdx={idx}
                        parentId={resourceItem?.rId}
                        setSecondaryMergeResource={setSecondaryMergeResource}
                        secondaryMergeResource={secondaryMergeResource}
                        setIsLoading={setIsLoaded}
                        errors={errors}
                        validateSecondaryResourceField={
                          validateSecondaryResourceField
                        }
                      />
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectedResourceList;
